import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import ExportTray from '@/assets/icons/export-tray.svg?react';
import { Progress } from '@/components/atoms/progress';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/tooltip';
import { DataTable, type DataTableColumnDef } from '@/components/molecules/DataTable';
import { ErrorBoundaryWrapper } from '@/features/api/components/ErrorBoundaryWrapper';
import { subscribeToProjectSSE } from '@/features/api/utils/subscribeToProjectSSE';
import { useGetProjectLocationsCSVMutation } from '@/features/locations/services/locations.api';
import { useMixpanel } from '@/hooks/useMixpanel';
import { ACTION_TRACKED, BUTTON_CLICKED } from '@/libs/mixpanel';
import { cn } from '@/libs/utils/cn';
import { type IProject, PROJECT_STATUS } from '@/types/api/IProject';
import {
  invalidAllProjectQueries,
  type ProjectPaginated,
  projectsKeyFactory,
  useCancelProject,
  useProjectsQuery,
} from '../services/project.api';

export function ProjectsList() {
  const [page, setPage] = useState<number>(1);
  const [downloadUrls, setDownloadUrls] = useState<Record<string, string>>({});

  // SSE tracking for newly created project
  const [trackedProjectId, setTrackedProjectId] = useState<string | null>(null);

  const trackAction = useMixpanel((state) => state.trackAction);
  const trackButtonClicked = useMixpanel((state) => state.trackButtonClicked);

  const { data, isFetching, isLoading, isError, refetch } = useProjectsQuery(page);
  const downloadMutation = useGetProjectLocationsCSVMutation();
  const cancelProjectMutation = useCancelProject();

  const queryClient = useQueryClient();

  function projectSSE(projectId: string) {
    return subscribeToProjectSSE(projectId, {
      setIsLoading: () => {}, // Not needed since we're tracking in the list
      setProgress: (project) => {
        // Update the project directly in the query cache
        queryClient.setQueryData(
          projectsKeyFactory.getAll(page).queryKey,
          (oldPaginatedResp: ProjectPaginated | undefined) => {
            if (!oldPaginatedResp) return oldPaginatedResp;

            return {
              pagination: oldPaginatedResp.pagination,
              projects: oldPaginatedResp.projects.map((p) =>
                p.id === project.id ? { ...p, ...project } : p,
              ),
            };
          },
        );
      },
      onSuccess: (project: IProject) => {
        toast.success('Project created');

        // Track project completion
        trackAction(ACTION_TRACKED.PROJECT_CREATED, {
          success: true,
          project_id: project.id,
        });

        // Clear tracked project ID and refresh list
        setTrackedProjectId(null);
        invalidAllProjectQueries(queryClient);
      },
      onError: (errorMessage: string) => {
        toast.error(errorMessage);

        // Track project creation failure
        trackAction(ACTION_TRACKED.PROJECT_CREATED, {
          success: false,
          project_id: projectId,
          error_message: errorMessage,
        });

        // Clear tracked project ID and refresh list
        setTrackedProjectId(null);
        invalidAllProjectQueries(queryClient);
      },
      onInterrupted: (reason) => {
        // Tracking stays set on a lost watch: clearing it would re-subscribe to the same running
        // project straight away and loop on an immediate failure.
        if (reason === 'cancelled') setTrackedProjectId(null);
        invalidAllProjectQueries(queryClient);
      },
    });
  }

  // Subscribe to SSE for the newly created project
  // biome-ignore lint/correctness/useExhaustiveDependencies: SSE not needed in dependencies
  useEffect(() => {
    // Find any running or pending project in the list
    const runningProject = data?.projects.find(
      (p) => p.status === PROJECT_STATUS.RUNNING || p.status === PROJECT_STATUS.PENDING,
    );

    if (runningProject && trackedProjectId !== runningProject.id) {
      setTrackedProjectId(runningProject.id);
    }
  }, [data?.projects, isFetching, trackedProjectId]);

  // Separate effect to handle SSE subscription based on trackedProjectId
  // biome-ignore lint/correctness/useExhaustiveDependencies: SSE not needed in dependencies
  useEffect(() => {
    if (!trackedProjectId) return;

    const sseController = projectSSE(trackedProjectId);

    // Cleanup: stop SSE when component unmounts or when trackedProjectId changes
    return () => {
      sseController.stop();
    };
  }, [trackedProjectId]);

  const triggerDownload = useCallback((url: string, projectId: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${projectId}-metrics.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  const handleDownloadCSV = useCallback(
    async (projectId: string) => {
      trackButtonClicked(BUTTON_CLICKED.DOWNLOAD_CSV, {
        project_id: projectId,
      });

      const cachedUrl = downloadUrls[projectId];

      if (!cachedUrl) {
        const blob = await downloadMutation.mutateAsync(projectId);
        const createdUrl = window.URL.createObjectURL(blob);

        setDownloadUrls((prev) => {
          const existing = prev[projectId];
          if (existing) window.URL.revokeObjectURL(existing);
          return { ...prev, [projectId]: createdUrl };
        });

        triggerDownload(createdUrl, projectId);
        return;
      }

      triggerDownload(cachedUrl, projectId);
      // TODO:
      // window.URL.revokeObjectURL(url);
    },
    [downloadMutation, downloadUrls, trackButtonClicked, triggerDownload],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need to trigger callback recreation
  const handleProjectCancel = useCallback(async (projectId: string) => {
    await cancelProjectMutation.mutateAsync(
      { projectId },
      {
        onSuccess: () => {
          trackAction(ACTION_TRACKED.PROJECT_CANCELLED, {
            success: true,
            project_id: projectId,
          });
        },
        onError: (error) => {
          trackAction(ACTION_TRACKED.PROJECT_CANCELLED, {
            success: false,
            project_id: projectId,
            error_message: error instanceof Error ? error.message : 'Unknown error',
          });
        },
      },
    );
  }, []);

  const columns = useMemo<DataTableColumnDef<IProject>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Project name',
        size: 350,
        cell: ({ row }) => {
          const projectName: IProject['name'] = row.getValue('name');
          return <div className="min-w-0 break-words">{projectName}</div>;
        },
      },
      {
        accessorKey: 'start_date',
        header: 'Created at',
        cell: ({ row }) => {
          const date = new Date(row.getValue('start_date'));
          return format(date, 'MMM d, yyyy - h:mmaaa').toLowerCase();
        },
      },
      {
        accessorKey: 'location_count',
        header: 'Locations',
        size: 100,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: trackedProjectId ? 300 : undefined,
        cell: ({ row }) => {
          const status: IProject['status'] = row.getValue('status');
          const project = row.original;

          if (status === PROJECT_STATUS.FAILED)
            return <span className="capitalize">Project failed - no locations analysed</span>;

          if (project.id === trackedProjectId && status === PROJECT_STATUS.RUNNING) {
            const progressPercentage = Math.round(project.progress * 100);
            return (
              <div className="flex gap-2 items-center">
                <span className="capitalize">{status}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Progress value={progressPercentage} className="w-full" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>progress: {progressPercentage}%</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            );
          }

          return <span className="capitalize">{status}</span>;
        },
      },
      {
        accessorKey: 'id',
        header: '',
        size: 80,
        cell: ({ row }) => {
          const projectId = row.original.id;
          const project = row.original;
          const isCompleted = project.status === PROJECT_STATUS.COMPLETED;

          // if project is running, action allow only to cancel it
          if (project.id === trackedProjectId && project.status === PROJECT_STATUS.RUNNING)
            return (
              <button
                type="button"
                className="flex text-brand-800 cursor-pointer gap-2 items-center w-full justify-end"
                onClick={() => handleProjectCancel(projectId)}
              >
                <X size={16} />
                Cancel
              </button>
            );

          return (
            <div key={projectId} className="flex gap-4.5 w-full justify-end">
              <button
                type="button"
                className="link font-normal no-underline cursor-pointer"
                onClick={() => handleDownloadCSV(projectId)}
                disabled={!isCompleted}
                aria-label="Download CSV"
                title="Download CSV"
              >
                <ExportTray
                  className={cn('size-4.5', isCompleted ? 'text-primary' : 'text-gray-400')}
                />
              </button>
            </div>
          );
        },
      },
    ],
    [handleDownloadCSV, handleProjectCancel, trackedProjectId],
  );

  const total = data?.pagination.total;
  const totalPages = data?.pagination.total_pages ?? 0;
  const currentPage = Math.min(page, Math.max(totalPages, 1));

  return (
    <ErrorBoundaryWrapper>
      {/* Same fixed height as Zone Management's filter-bar wrapper (no filters here) */}
      <div className="mb-4 min-h-0 sm:min-h-10" />
      {(total !== undefined || totalPages > 1) && (
        <div
          aria-live="polite"
          className="mt-4 flex items-center justify-between text-sm text-muted-foreground"
        >
          <span>{totalPages > 1 && `Page ${currentPage} of ${totalPages}`}</span>
          <span>{total !== undefined && `${total} project${total === 1 ? '' : 's'}`}</span>
        </div>
      )}
      <DataTable
        className="mt-6"
        columns={columns}
        data={data?.projects ?? []}
        isLoading={isLoading}
        isError={isError}
        errorMessage="Failed to load projects"
        errorDescription="An error occurred while fetching your projects. Please try again."
        onRetry={() => refetch()}
        pagination={{
          currentPage: page,
          totalPages,
          onPageChange: (newPage) => {
            setPage(newPage);
          },
        }}
      />
    </ErrorBoundaryWrapper>
  );
}
